import { countLabel } from 'utils/ru';
import { formatNumber } from 'i18n/format';
import { formatDate as localeDate } from 'i18n/format';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  ClickAwayListener,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { t } from 'i18n';
import { useTranslation } from 'react-i18next';

// Layered drawing without a graph library: layer = longest path from a raw
// table, rows ordered by the barycenter of the predecessors. Filters hide
// nodes and edges are re-routed through the hidden ones.

const ROW = 44;
const LEAF_ROW = 60;
const NODE_H = 30;
const LEAF_H = 48;
const GAP = 48;
const PAD = 16;
const MAX_H = 560; // the scroll box; 'fit' aims at it
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 2;
const ARROW = 7;
const PROBLEMS = new Set(['waiting', 'backing_off']);
/**
 * Categorical colours — one per policy, one per source pipeline; the only
 * graph colours outside the palette, so they carry two sets: 700-level hues
 * sink into slate (#1976d2 lands at 2.3:1 there), so dark takes the 300-level
 * of the same Material hue and a source keeps its identity across modes.
 */
const POLICY_COLORS_LIGHT = { any: '#7b1fa2', max_wait: '#3949ab', degrade: '#00897b' };
const POLICY_COLORS_DARK  = { any: '#ce93d8', max_wait: '#9fa8da', degrade: '#4db6ac' };
const SOURCE_PALETTE_LIGHT = ['#1976d2', '#7b1fa2', '#00897b', '#f57c00', '#c2185b', '#5d4037', '#455a64', '#afb42b'];
const SOURCE_PALETTE_DARK  = ['#64b5f6', '#ce93d8', '#4db6ac', '#ffb74d', '#f06292', '#bcaaa4', '#b0bec5', '#dce775'];

function ago(iso) {
  if (!iso) return t('data.neverLanded');
  const minutes = Math.round((Date.now() - new Date(iso)) / 60000);
  if (minutes < 1) return t('data.justNow');
  if (minutes < 60) return t('data.minutesAgo', { count: minutes, amount: formatNumber(minutes) });
  const hours = Math.round(minutes / 60);
  if (hours < 48) return t('data.hoursAgo', { count: hours, amount: formatNumber(hours) });
  return t('data.daysAgo', { count: Math.round(hours / 24), amount: formatNumber(Math.round(hours / 24)) });
}

function forDuration(iso) {
  const minutes = Math.round((Date.now() - new Date(iso)) / 60000);
  if (minutes < 1) return t('data.underAMinute');
  if (minutes < 60) return t('data.minuteShort', { amount: formatNumber(minutes) });
  const hours = Math.round(minutes / 60);
  if (hours < 48) return t('data.hourShort', { amount: formatNumber(hours) });
  return t('data.dayShort', { amount: formatNumber(Math.round(hours / 24)) });
}

function formatDate(iso) {
  if (!iso) return '—';
  return localeDate(new Date(iso), { dateStyle: 'medium', timeStyle: 'short' });
}

function textWidth(label, size = 11) {
  return Math.ceil(String(label).length * size * 0.62);
}

function policyBadge(policy) {
  if (!policy) return '';
  const parts = [];
  if (policy.readiness === 'any') parts.push('any');
  if (policy.max_wait) parts.push(String(policy.max_wait));
  if (policy.on_failure) parts.push(policy.on_failure);
  return parts.join(' · ');
}

function leafNote(leaf, holds) {
  const when = leaf.loading ? t('data.loadingNow') : leaf.landed_at ? t('data.landedValue', { value1: ago(leaf.landed_at) }) : t('data.neverLanded');
  const landed = holds.length ? t('data.valueHoldsValue', { value1: when, value2: holds.length }) : when;
  return `${landed}${leaf.optional ? ' · optional' : ''}`;
}

const VERDICT_LABEL = {
  get up_to_date() { return t('data.upToDate2'); },
  get ready() { return t('data.readyToBuild2'); },
  batching: 'batching',
  waiting: 'waiting',
  get input_loading() { return t('data.inputLoading2'); },
  get backing_off() { return t('data.failedBackingOff2'); },
};

// the full map: what reads what
function fullGraph(nodes, leaves) {
  const byName = new Map(nodes.map((n) => [n.name, n]));
  const leafSet = new Set(leaves.map((l) => l.name));
  const parentsOf = new Map();
  const directLeavesOf = new Map();
  const childrenOf = new Map();
  nodes.forEach((n) => {
    const parents = (n.parents || []).filter((p) => byName.has(p));
    parentsOf.set(n.name, parents);
    const covered = new Set();
    parents.forEach((p) => (byName.get(p).leaves || []).forEach((l) => covered.add(l)));
    directLeavesOf.set(n.name, (n.leaves || []).filter((l) => !covered.has(l) && leafSet.has(l)));
    parents.forEach((p) => {
      if (!childrenOf.has(p)) childrenOf.set(p, []);
      childrenOf.get(p).push(n.name);
    });
  });
  const readersOfLeaf = new Map(); // leaf -> nodes reading it anywhere in their closure
  nodes.forEach((n) => (n.leaves || []).forEach((l) => {
    if (!readersOfLeaf.has(l)) readersOfLeaf.set(l, []);
    readersOfLeaf.get(l).push(n.name);
  }));
  const closure = (start, step) => {
    const seen = new Set();
    const stack = [...start];
    while (stack.length) {
      const cur = stack.pop();
      (step(cur) || []).forEach((next) => {
        if (!seen.has(next)) {
          seen.add(next);
          stack.push(next);
        }
      });
    }
    return seen;
  };
  return {
    byName,
    parentsOf,
    directLeavesOf,
    childrenOf,
    readersOfLeaf,
    upstreamNodes: (names) => closure(names, (x) => parentsOf.get(x)),
    downstreamNodes: (names) => closure(names, (x) => childrenOf.get(x)),
  };
}

// the drawn graph: visible elements only, hidden ones bypassed
function layout(full, nodes, leaves, visibleNodes, allowedLeaves, widthOf) {
  const memo = new Map();
  const sourcesOf = (name) => {
    if (memo.has(name)) return memo.get(name);
    const out = new Map(); // key -> {kind, name}
    (full.parentsOf.get(name) || []).forEach((p) => {
      if (visibleNodes.has(p)) out.set(`n:${p}`, { kind: 'node', name: p });
      else sourcesOf(p).forEach((v, k) => out.set(k, v));
    });
    (full.directLeavesOf.get(name) || []).forEach((l) => {
      if (allowedLeaves.has(l)) out.set(`l:${l}`, { kind: 'leaf', name: l });
    });
    memo.set(name, out);
    return out;
  };

  const edges = [];
  const usedLeaves = new Set();
  const preds = new Map();
  const succs = new Map();
  visibleNodes.forEach((name) => {
    sourcesOf(name).forEach((src) => {
      edges.push({ from: src.name, to: name, kind: src.kind });
      if (src.kind === 'leaf') usedLeaves.add(src.name);
      if (!preds.has(name)) preds.set(name, []);
      preds.get(name).push(src.name);
      if (!succs.has(src.name)) succs.set(src.name, []);
      succs.get(src.name).push(name);
    });
  });

  const layerOf = new Map([...usedLeaves].map((l) => [l, 0]));
  const visiting = new Set();
  const layer = (name) => {
    if (layerOf.has(name)) return layerOf.get(name);
    if (visiting.has(name)) return 1;
    visiting.add(name);
    const above = (preds.get(name) || []).map(layer);
    const value = above.length ? Math.max(...above) + 1 : 1;
    layerOf.set(name, value);
    visiting.delete(name);
    return value;
  };
  visibleNodes.forEach(layer);

  // column 0 must exist even when nothing is visible: it is sorted below
  const columns = [[]];
  const put = (name, c) => {
    columns[c] = columns[c] || [];
    columns[c].push(name);
  };
  usedLeaves.forEach((l) => put(l, 0));
  visibleNodes.forEach((n) => put(n, layerOf.get(n)));
  for (let i = 0; i < columns.length; i += 1) columns[i] = columns[i] || [];

  const rowOf = new Map();
  columns[0].sort().forEach((name, i) => rowOf.set(name, i));
  for (let c = 1; c < columns.length; c += 1) {
    const score = (name) => {
      const rows = (preds.get(name) || []).map((p) => rowOf.get(p)).filter((r) => r !== undefined);
      return rows.length ? rows.reduce((a, b) => a + b, 0) / rows.length : Number.MAX_SAFE_INTEGER;
    };
    columns[c].sort((a, b) => score(a) - score(b) || a.localeCompare(b));
    columns[c].forEach((name, i) => rowOf.set(name, i));
  }

  const widths = columns.map((names) => Math.max(120, ...names.map(widthOf)));
  const pitch = (c) => (c === 0 ? LEAF_ROW : ROW);
  const inner = Math.max(ROW, ...columns.map((names, c) => names.length * pitch(c)));
  const xOf = [];
  let x = PAD;
  widths.forEach((w) => {
    xOf.push(x);
    x += w + GAP;
  });
  const positions = new Map();
  columns.forEach((names, c) => {
    const offset = (inner - names.length * pitch(c)) / 2;
    names.forEach((name, i) => positions.set(name, { x: xOf[c], y: PAD + offset + i * pitch(c), w: widths[c] }));
  });

  const closure = (start, step) => {
    const seen = new Set();
    const stack = [start];
    while (stack.length) {
      const cur = stack.pop();
      (step.get(cur) || []).forEach((next) => {
        if (!seen.has(next)) {
          seen.add(next);
          stack.push(next);
        }
      });
    }
    return seen;
  };
  return {
    edges,
    positions,
    usedLeaves,
    width: x - GAP + PAD,
    height: PAD * 2 + inner,
    upstream: (name) => closure(name, preds),
    downstream: (name) => closure(name, succs),
  };
}

function Tag({ color, children }) {
  useTranslation();
  return (
    <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
      <Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', bgcolor: color, mr: 0.5, verticalAlign: 'middle' }} />
      {children}
    </Box>
  );
}

// [label, colour] of a table's or a node's state, for the card and the menu
function statusOf(kind, item, theme, colors) {
  if (kind === 'leaf') {
    if (item.loading) return [t('data.loadingNow'), theme.palette.info.main];
    if (!item.landed_at) return [t('data.neverLanded'), item.optional ? theme.palette.grey[500] : theme.palette.warning.main];
    if (item.holds?.length) return [t('data.holdsValue', { value1: item.holds.length }), theme.palette.warning.main];
    return [t('data.landed'), theme.palette.success.main];
  }
  if (item.reset) return [t('data.resetRebuildingOnTheNextTick'), theme.palette.grey[900]];
  return [VERDICT_LABEL[item.verdict] || item.verdict, colors.verdict[item.verdict] || theme.palette.grey[500]];
}

function StatusPill({ status }) {
  useTranslation();
  return (
    <Box component="span" sx={{ fontSize: 10, fontWeight: 600, px: 0.75, py: 0.125, borderRadius: 1, whiteSpace: 'nowrap', color: status[1], border: '1px solid', borderColor: status[1], bgcolor: alpha(status[1], 0.12) }}>
      {status[0]}
    </Box>
  );
}

// one line under the name in the menu header
function statusLine(kind, item) {
  if (kind === 'leaf') return leafNote(item, item.holds || []);
  if (item.reset) return t('data.rebuildingOnTheNextTick');
  if (item.verdict === 'waiting' && item.waiting_since) return t('data.waitingForValue', { value1: forDuration(item.waiting_since) });
  return item.last_success_at ? t('data.lastSuccessValue', { value1: formatDate(item.last_success_at) }) : t('data.neverBuilt');
}

function HoverCard({ hover, theme, colors, pos, cardRef }) {
  const { t } = useTranslation();
  if (!hover) return null;
  const { item, kind } = hover;
  const status = statusOf(kind, item, theme, colors);
  const rows = kind === 'leaf'
    ? [
      [t('data.landed'), item.loading ? t('data.loadingNow') : item.landed_at ? `${ago(item.landed_at)} (${formatDate(item.landed_at)})` : 'never'],
      [t('data.rows2'), item.rows ?? '—'],
      [t('data.source'), item.pipeline ? <Tag color={colors.source.get(item.pipeline.name)}>{item.pipeline.name}</Tag> : t('data.noPipelineLandsIt')],
      [t('data.readBy2'), countLabel(item.readers, 'node')],
      item.optional ? [t('data.policy'), t('data.optionalNeverHoldsAMart')] : null,
      item.holds?.length ? [t('data.holds'), item.holds.join(', ')] : null,
    ]
    : [
      item.policy && policyBadge(item.policy) ? [t('data.policy'), policyBadge(item.policy)] : null,
      [t('data.lastSuccess2'), formatDate(item.last_success_at)],
      item.last_status ? [t('data.lastResult2'), item.last_status] : null,
      item.waiting_for?.length ? [t('data.waitingFor2'), item.waiting_for.map((w) => `${w.leaf} (${w.loading ? t('data.loadingNow') : w.landed_at ? t('data.lastLandedValue', { value1: ago(w.landed_at) }) : t('data.neverLanded')})`).join(', ')] : null,
      item.waiting_since && item.verdict === 'waiting' ? [t('data.held'), t('data.forValuevalue', { value1: forDuration(item.waiting_since), value2: item.overdue ? t('data.overMaxWait') : '' })] : null,
      item.code_changed ? [t('data.code'), t('data.changedSinceTheLastBuild')] : null,
      item.never_built ? [t('data.history2'), t('data.neverBuilt')] : null,
      item.sources?.length ? [t('data.sources2'), (
        <Box component="span" sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 1 }}>
          {item.sources.map((name) => <Tag key={name} color={colors.source.get(name)}>{name}</Tag>)}
        </Box>
      )] : null,
    ];
  return (
    <Paper
      ref={cardRef}
      elevation={4}
      sx={{ position: 'absolute', left: pos.x + 14, top: pos.y + 14, zIndex: 3, p: 1.25, maxWidth: 360, pointerEvents: 'none', borderRadius: 1.5 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 0.5 }}>
        <Typography variant="caption" fontWeight={700} fontFamily="monospace">
          {item.name}{kind === 'node' && item.resource_type !== 'model' ? ` (${item.resource_type})` : ''}
        </Typography>
        <StatusPill status={status} />
      </Box>
      {rows.filter(Boolean).map(([k, v]) => (
        <Box key={k} sx={{ display: 'flex', gap: 1, fontSize: 11, lineHeight: 1.5 }}>
          <Box component="span" sx={{ color: theme.palette.text.secondary, minWidth: 78, flexShrink: 0 }}>{k}</Box>
          <Box component="span" sx={{ wordBreak: 'break-word' }}>{v}</Box>
        </Box>
      ))}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontSize: 10 }}>{t('data.leftClickLinkedNodesRightClickOptions')}</Typography>
    </Paper>
  );
}

// hover lives here, so pointing at boxes never re-renders the graph
const HoverLayer = forwardRef(function HoverLayer({ theme, colors }, ref) {
  useTranslation();
  const [hover, setHover] = useState(null);
  const cardRef = useRef(null);
  const posRef = useRef({ x: 0, y: 0 });
  useImperativeHandle(ref, () => ({
    show: (kind, item, pos) => {
      posRef.current = pos;
      setHover({ kind, item });
    },
    move: (pos) => {
      posRef.current = pos;
      const el = cardRef.current;
      if (el) {
        el.style.left = `${pos.x + 14}px`;
        el.style.top = `${pos.y + 14}px`;
      }
    },
    hide: () => setHover(null),
  }), []);
  return <HoverCard hover={hover} pos={posRef.current} cardRef={cardRef} theme={theme} colors={colors} />;
});

export default function ReadinessGraph({ nodes, leaves, onReset }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const POLICY_COLORS = dark ? POLICY_COLORS_DARK : POLICY_COLORS_LIGHT;
  const SOURCE_PALETTE = dark ? SOURCE_PALETTE_DARK : SOURCE_PALETTE_LIGHT;
  const navigate = useNavigate();
  const wrapperRef = useRef(null);
  const scrollRef = useRef(null);
  const hoverRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [selected, setSelected] = useState(null); // {kind, name}
  const [menu, setMenu] = useState(null); // {kind, name, x, y}
  const [query, setQuery] = useState('');
  const [problemsOnly, setProblemsOnly] = useState(false);
  const [collapseStaging, setCollapseStaging] = useState(false);
  const [source, setSource] = useState('');
  const [colorBy, setColorBy] = useState('verdict');

  const full = useMemo(() => fullGraph(nodes, leaves), [nodes, leaves]);
  const leafByName = useMemo(() => new Map(leaves.map((l) => [l.name, l])), [leaves]);
  const pipelines = useMemo(
    () => [...new Set(leaves.map((l) => l.pipeline?.name).filter(Boolean))].sort(),
    [leaves],
  );

  const sourceColor = useMemo(
    () => new Map(pipelines.map((p, i) => [p, SOURCE_PALETTE[i % SOURCE_PALETTE.length]])),
    [pipelines, SOURCE_PALETTE],
  );
  const sourcesOfNode = useMemo(() => {
    const out = new Map();
    nodes.forEach((n) => {
      const names = new Set((n.leaves || []).map((l) => leafByName.get(l)?.pipeline?.name).filter(Boolean));
      out.set(n.name, [...names].sort());
    });
    return out;
  }, [nodes, leafByName]);
  const waitedBy = useMemo(() => {
    const out = new Map();
    nodes.forEach((n) => {
      if (n.verdict !== 'waiting') return;
      (n.waiting_for || []).forEach((w) => {
        if (!out.has(w.leaf)) out.set(w.leaf, []);
        out.get(w.leaf).push(n.name);
      });
    });
    return out;
  }, [nodes]);

  const { visibleNodes, allowedLeaves } = useMemo(() => {
    let visible = new Set(nodes.map((n) => n.name));
    let allowed = new Set(leaves.map((l) => l.name));
    const q = query.trim().toLowerCase();
    if (q) {
      const matchedNodes = nodes.filter((n) => n.name.toLowerCase().includes(q)).map((n) => n.name);
      const matchedLeaves = leaves.filter((l) => l.name.toLowerCase().includes(q)).map((l) => l.name);
      const readers = matchedLeaves.flatMap((l) => full.readersOfLeaf.get(l) || []);
      const keep = new Set([
        ...matchedNodes,
        ...full.upstreamNodes(matchedNodes),
        ...full.downstreamNodes([...matchedNodes, ...readers]),
        ...readers,
      ]);
      visible = new Set([...visible].filter((n) => keep.has(n)));
    }
    if (problemsOnly) {
      visible = new Set([...visible].filter((n) => PROBLEMS.has(full.byName.get(n)?.verdict)));
    }
    if (source) {
      const sourceLeaves = leaves.filter((l) => l.pipeline?.name === source).map((l) => l.name);
      allowed = new Set(sourceLeaves);
      const readers = new Set(sourceLeaves.flatMap((l) => full.readersOfLeaf.get(l) || []));
      visible = new Set([...visible].filter((n) => readers.has(n)));
    }
    if (collapseStaging) {
      visible = new Set([...visible].filter((n) => {
        const node = full.byName.get(n);
        const readsTablesOnly = (full.parentsOf.get(n) || []).length === 0 && (full.directLeavesOf.get(n) || []).length > 0;
        const hasChildren = (full.childrenOf.get(n) || []).length > 0;
        return !(node?.resource_type === 'model' && readsTablesOnly && hasChildren);
      }));
    }
    return { visibleNodes: visible, allowedLeaves: allowed };
  }, [nodes, leaves, full, query, problemsOnly, source, collapseStaging]);

  const widthOf = useMemo(() => (name) => {
    const node = full.byName.get(name);
    if (!node) {
      const leaf = leafByName.get(name);
      if (!leaf) return 120;
      return 24 + Math.max(
        textWidth(name),
        10 + textWidth(leaf.pipeline?.name || t('data.noPipelineLandsIt'), 10),
        textWidth(leafNote(leaf, waitedBy.get(name) || []), 10),
      );
    }
    const badge = policyBadge(node.policy);
    const dots = Math.min((sourcesOfNode.get(name) || []).length, 5);
    return 24 + Math.max(
      120 - 24,
      textWidth(name) + (badge ? textWidth(badge, 9) + 18 : 0) + (dots > 1 ? dots * 9 + 8 : 0),
    );
  }, [full, leafByName, waitedBy, sourcesOfNode, t]);
  const graph = useMemo(
    () => layout(full, nodes, leaves, visibleNodes, allowedLeaves, widthOf),
    [full, nodes, leaves, visibleNodes, allowedLeaves, widthOf],
  );
  // the canvas keeps the height of the whole graph at 100%: neither the zoom nor a filter moves the page
  const canvasHeight = useMemo(() => {
    const whole = layout(full, nodes, leaves, new Set(nodes.map((n) => n.name)), new Set(leaves.map((l) => l.name)), widthOf);
    return Math.min(whole.height + 2, MAX_H);
  }, [full, nodes, leaves, widthOf]);

  // outgoing edges fan along the box's edge (sorted by the far end's row so
  // the fan does not cross itself); incoming ones converge into one arrowhead
  const ports = useMemo(() => {
    const outgoing = new Map();
    graph.edges.forEach((e) => {
      if (!outgoing.has(e.from)) outgoing.set(e.from, []);
      outgoing.get(e.from).push(e);
    });
    const yOf = (name) => graph.positions.get(name)?.y || 0;
    outgoing.forEach((list) => list.sort((p, q) => yOf(p.to) - yOf(q.to)));
    return {
      outOffset: (e, h) => {
        const list = outgoing.get(e.from) || [];
        const k = list.length;
        if (k <= 1) return 0;
        return (list.indexOf(e) - (k - 1) / 2) * Math.min(7, (h - 12) / (k - 1));
      },
    };
  }, [graph]);

  const clampZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));
  const zoomBy = (factor) => setZoom((z) => clampZoom(z * factor));
  const fitZoom = () => {
    const el = scrollRef.current;
    if (!el || !graph.width || !graph.height) return;
    setZoom(clampZoom(Math.min((el.clientWidth - 2) / graph.width, (el.clientHeight - 2) / graph.height)));
  };
  const hasNodes = nodes.length > 0;
  useEffect(() => {
    // a wheel listener that may preventDefault blocks threaded scrolling, so
    // it exists only while Ctrl (or Cmd) is held; plain scrolling stays smooth
    const el = scrollRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z * (e.deltaY < 0 ? 1.1 : 1 / 1.1))));
    };
    let armed = false;
    const arm = (e) => {
      if (armed || (e.key !== 'Control' && e.key !== 'Meta')) return;
      armed = true;
      el.addEventListener('wheel', onWheel, { passive: false });
    };
    const disarm = (e) => {
      if (!armed || (e?.type === 'keyup' && e.key !== 'Control' && e.key !== 'Meta')) return;
      armed = false;
      el.removeEventListener('wheel', onWheel);
    };
    window.addEventListener('keydown', arm);
    window.addEventListener('keyup', disarm);
    window.addEventListener('blur', disarm);
    return () => {
      window.removeEventListener('keydown', arm);
      window.removeEventListener('keyup', disarm);
      window.removeEventListener('blur', disarm);
      disarm();
    };
  }, [hasNodes]);

  const verdictColor = {
    up_to_date: theme.palette.success.main,
    ready: theme.palette.info.main,
    batching: theme.palette.info.light,
    waiting: theme.palette.warning.main,
    input_loading: theme.palette.grey[500],
    backing_off: theme.palette.error.main,
  };
  const policyColor = (policy) => {
    if (policy?.on_failure === 'degrade') return POLICY_COLORS.degrade;
    if (policy?.readiness === 'any') return POLICY_COLORS.any;
    if (policy?.max_wait) return POLICY_COLORS.max_wait;
    return theme.palette.grey[500];
  };
  const nodeColor = (n) => {
    if (colorBy === 'source') {
      const srcs = sourcesOfNode.get(n.name) || [];
      return srcs.length === 1 ? sourceColor.get(srcs[0]) : theme.palette.grey[500];
    }
    if (colorBy === 'policy') return policyColor(n.policy);
    if (n.reset) return theme.palette.grey[900]; // reset by hand, the planner has it next
    return verdictColor[n.verdict] || theme.palette.grey[500];
  };
  const leafColor = (leaf, hot) => {
    if (colorBy === 'source' && leaf.pipeline) return sourceColor.get(leaf.pipeline.name);
    if (colorBy === 'policy') return leaf.optional ? POLICY_COLORS.any : theme.palette.grey[500];
    return hot ? theme.palette.warning.main : theme.palette.grey[500];
  };

  const lit = selected
    ? new Set([selected.name, ...graph.upstream(selected.name), ...graph.downstream(selected.name)])
    : null;
  const waitingEdges = new Set();
  if (colorBy === 'verdict') {
    nodes.forEach((n) => {
      if (n.verdict === 'waiting') (n.waiting_for || []).forEach((w) => waitingEdges.add(`${w.leaf}->${n.name}`));
    });
  }

  const edgeStyle = (e) => {
    const hot = waitingEdges.has(`${e.from}->${e.to}`);
    const litEdge = Boolean(lit && lit.has(e.from) && lit.has(e.to));
    const dim = lit && !litEdge;
    let stroke = theme.palette.grey[500];
    if (hot) stroke = theme.palette.warning.main;
    else if (litEdge) stroke = theme.palette.grey[700];
    let opacity = 0.3;
    if (dim) opacity = 0.06;
    else if (litEdge) opacity = 0.9;
    else if (hot) opacity = 0.8;
    return { hot, stroke, opacity, width: hot ? 1.5 : litEdge ? 1.4 : 1 };
  };
  const place = (e) => {
    const rect = wrapperRef.current?.getBoundingClientRect();
    return { x: e.clientX - (rect?.left || 0), y: e.clientY - (rect?.top || 0) };
  };
  const showCard = (e, kind, item) => {
    if (!menu) hoverRef.current?.show(kind, item, place(e));
  };
  const hideCard = () => hoverRef.current?.hide();
  // drag the canvas to pan it; a drag that moved swallows the click it ends with
  const drag = useRef(null);
  const startPan = (e) => {
    const el = scrollRef.current;
    if (e.button !== 0 || !el) return;
    drag.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop, moved: false };
    el.classList.add('panning');
    const move = (ev) => {
      const d = drag.current;
      if (!d) return;
      const dx = ev.clientX - d.x;
      const dy = ev.clientY - d.y;
      if (!d.moved && Math.abs(dx) + Math.abs(dy) < 4) return;
      d.moved = true;
      el.scrollLeft = d.left - dx;
      el.scrollTop = d.top - dy;
    };
    const stop = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', stop);
      el.classList.remove('panning');
      setTimeout(() => { drag.current = null; }, 0); // after the click that follows the mouseup
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', stop);
  };
  const swallowClickAfterPan = (e) => {
    if (drag.current?.moved) e.stopPropagation();
  };
  const moveCard = (e) => {
    if (!menu) hoverRef.current?.move(place(e));
  };
  const hint = selected
    ? t('data.valueValueUpstreamValueDownstreamLitClickTheBackgroundToClear', { value1: selected.name, value2: graph.upstream(selected.name).size, value3: graph.downstream(selected.name).size })
    : t('data.leftClickANodeToSeeWhatItIsLinkedTo');

  const menuItem = menu && (menu.kind === 'leaf'
    ? (leafByName.has(menu.name) ? { ...leafByName.get(menu.name), holds: waitedBy.get(menu.name) || [] } : null)
    : (full.byName.has(menu.name) ? { ...full.byName.get(menu.name), sources: sourcesOfNode.get(menu.name) || [] } : null));

  if (!nodes.length) return null;


  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
        <TextField size="small" placeholder={t('data.findAModelOrTable')} value={query} onChange={(e) => setQuery(e.target.value)} sx={{ width: 220 }} inputProps={{ 'aria-label': t('data.findAModelOrTable') }} />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="readiness-source-label">{t('data.source3')}</InputLabel>
          <Select labelId="readiness-source-label" label={t('data.source3')} value={source} onChange={(e) => setSource(e.target.value)}>
            <MenuItem value=""><em>{t('data.allSources')}</em></MenuItem>
            {pipelines.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </Select>
        </FormControl>
        <Chip size="small" label={t('data.problemsOnly')} color={problemsOnly ? 'warning' : 'default'} variant={problemsOnly ? 'filled' : 'outlined'} onClick={() => setProblemsOnly((v) => !v)} />
        <Chip size="small" label={t('data.collapseStaging')} color={collapseStaging ? 'primary' : 'default'} variant={collapseStaging ? 'filled' : 'outlined'} onClick={() => setCollapseStaging((v) => !v)} />
        <ToggleButtonGroup size="small" exclusive value={colorBy} onChange={(_, v) => v && setColorBy(v)} aria-label={t('data.colorBy')}>
          <ToggleButton value="verdict">{t('data.byState')}</ToggleButton>
          <ToggleButton value="source">{t('data.bySource')}</ToggleButton>
          <ToggleButton value="policy">{t('data.byPolicy')}</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary">
          {visibleNodes.size}{' '}{t('data.of')}{' '}{nodes.length}{' '}{t('data.nodes')}{' '}{graph.usedLeaves.size}{' '}{t('data.of')}{' '}{leaves.length}{' '}{t('data.tables3')}</Typography>
      </Box>
      <Box ref={wrapperRef} sx={{ position: 'relative' }}>
        <HoverLayer ref={hoverRef} theme={theme} colors={{ verdict: verdictColor, source: sourceColor }} />
        <Typography
          variant="caption"
          sx={{ position: 'absolute', top: 10, right: 22, zIndex: 2, pointerEvents: 'none', color: 'text.disabled' }}
        >
          {hint}
        </Typography>
        <Paper elevation={1} sx={{ position: 'absolute', bottom: 22, right: 22, zIndex: 2, display: 'flex', alignItems: 'center', gap: 1, px: 1, borderRadius: 1.5 }}>
          <Tooltip title={t('data.zoomOutCtrlWheel')}><span><IconButton size="small" onClick={() => zoomBy(1 / 1.25)} disabled={zoom <= ZOOM_MIN}><ZoomOutIcon fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title={t('data.backTo100')}><Typography variant="caption" color="text.secondary" sx={{ cursor: 'pointer', minWidth: 36, textAlign: 'center' }} onClick={() => setZoom(1)}>{Math.round(zoom * 100)}%</Typography></Tooltip>
          <Tooltip title={t('data.zoomInCtrlWheel')}><span><IconButton size="small" onClick={() => zoomBy(1.25)} disabled={zoom >= ZOOM_MAX}><ZoomInIcon fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title={t('data.fitTheWholeGraph')}><IconButton size="small" onClick={fitZoom}><FitScreenIcon fontSize="small" /></IconButton></Tooltip>
        </Paper>
        <Box
          ref={scrollRef}
          onClick={() => setSelected(null)}
          onContextMenu={(e) => { e.preventDefault(); setMenu(null); }}
          onMouseDown={startPan}
          onClickCapture={swallowClickAfterPan}
          sx={{
            overflow: 'auto',
            userSelect: 'none',
            '&.panning, &.panning *': { cursor: 'grabbing !important' },
            height: canvasHeight,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'grey.50',
            backgroundImage: `radial-gradient(${theme.palette.grey[300]} 1px, transparent 1.2px)`,
            backgroundSize: '18px 18px',
          }}
        >
          {visibleNodes.size === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>{t('data.nothingMatchesTheseFilters')}</Typography>
          ) : (
            <svg
              width={graph.width * zoom}
              height={graph.height * zoom}
              viewBox={`0 0 ${graph.width} ${graph.height}`}
              style={{ display: 'block', fontFamily: 'ui-monospace, Consolas, monospace' }}
            >
              {graph.edges.map((e) => {
                const a = graph.positions.get(e.from);
                const b = graph.positions.get(e.to);
                if (!a || !b) return null;
                const ah = e.kind === 'leaf' ? LEAF_H : NODE_H;
                const x1 = a.x + a.w;
                const y1 = a.y + ah / 2 + ports.outOffset(e, ah);
                const x2 = b.x - ARROW;
                const y2 = b.y + NODE_H / 2;
                const mid = (x1 + x2) / 2;
                const style = edgeStyle(e);
                return (
                  <path
                    key={`${e.from}->${e.to}`}
                    d={`M${x1},${y1} C${mid},${y1} ${mid},${y2} ${x2},${y2}`}
                    fill="none"
                    stroke={style.stroke}
                    strokeWidth={style.width}
                    strokeDasharray={style.hot ? '5 3' : undefined}
                    opacity={style.opacity}
                  />
                );
              })}
              {[...visibleNodes].map((name) => {
                const b = graph.positions.get(name);
                const into = graph.edges.filter((e) => e.to === name && graph.positions.has(e.from));
                if (!b || !into.length) return null;
                const style = into.map(edgeStyle).reduce((best, cur) => (cur.opacity > best.opacity ? cur : best));
                const cy = b.y + NODE_H / 2;
                return (
                  <path
                    key={`arrow:${name}`}
                    d={`M${b.x - ARROW},${cy - 4} L${b.x},${cy} L${b.x - ARROW},${cy + 4} z`}
                    fill={style.stroke}
                    opacity={Math.max(style.opacity, style.opacity > 0.06 ? 0.55 : 0)}
                  />
                );
              })}
              {leaves.map((leaf) => {
                const p = graph.positions.get(leaf.name);
                if (!p) return null;
                const holds = waitedBy.get(leaf.name) || [];
                const never = !leaf.landed_at && !leaf.optional;
                const hot = never || holds.length > 0;
                const color = leafColor(leaf, hot);
                const dim = lit && !lit.has(leaf.name);
                const isSelected = selected?.name === leaf.name;
                const note = leafNote(leaf, holds);
                const item = { ...leaf, holds };
                return (
                  <g
                    key={leaf.name}
                    opacity={dim ? 0.25 : 1}
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => { e.stopPropagation(); setSelected(isSelected ? null : { kind: 'leaf', name: leaf.name }); }}
                    onMouseEnter={(e) => showCard(e, 'leaf', item)}
                    onMouseMove={moveCard}
                    onMouseLeave={hideCard}
                    onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); hideCard(); setMenu({ kind: 'leaf', name: leaf.name, x: e.clientX, y: e.clientY }); }}
                    role="button"
                    aria-label={`${leaf.name}: ${note}`}
                  >
                    <rect x={p.x} y={p.y} width={p.w} height={LEAF_H} rx={6} fill={theme.palette.background.paper} />
                    <rect x={p.x} y={p.y} width={p.w} height={LEAF_H} rx={6} fill={hot && colorBy === 'verdict' ? theme.palette.warning.light : theme.palette.background.paper} fillOpacity={hot && colorBy === 'verdict' ? 0.15 : 1} stroke={color} strokeWidth={isSelected ? 2.5 : hot ? 1.8 : 1} strokeDasharray="4 2" />
                    <text x={p.x + 9} y={p.y + 15} fontSize={11} fill={theme.palette.text.primary}>{leaf.name}</text>
                    {leaf.pipeline ? (
                      <g>
                        <circle cx={p.x + 12} cy={p.y + 25} r={3} fill={sourceColor.get(leaf.pipeline.name)} />
                        <text x={p.x + 19} y={p.y + 28} fontSize={10} fill={colorBy === 'source' ? sourceColor.get(leaf.pipeline.name) : theme.palette.text.primary}>{leaf.pipeline.name}</text>
                      </g>
                    ) : (
                      <text x={p.x + 9} y={p.y + 28} fontSize={10} fill={theme.palette.text.disabled}>{t('data.noPipelineLandsIt')}</text>
                    )}
                    <text x={p.x + 9} y={p.y + 41} fontSize={10} fill={hot && colorBy === 'verdict' ? theme.palette.warning.dark : theme.palette.text.secondary}>
                      {note}
                    </text>
                  </g>
                );
              })}
              {nodes.map((n) => {
                const p = graph.positions.get(n.name);
                if (!p) return null;
                const color = nodeColor(n);
                const dim = lit && !lit.has(n.name);
                const isSelected = selected?.name === n.name;
                const badge = policyBadge(n.policy);
                const srcs = sourcesOfNode.get(n.name) || [];
                const item = { ...n, sources: srcs };
                const badgeW = badge ? textWidth(badge, 9) + 10 : 0;
                const dotsW = colorBy === 'source' && srcs.length > 1 ? Math.min(srcs.length, 5) * 9 + 4 : 0;
                return (
                  <g
                    key={n.name}
                    opacity={dim ? 0.25 : 1}
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => { e.stopPropagation(); setSelected(isSelected ? null : { kind: 'node', name: n.name }); }}
                    onMouseEnter={(e) => showCard(e, 'node', item)}
                    onMouseMove={moveCard}
                    onMouseLeave={hideCard}
                    onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); hideCard(); setMenu({ kind: 'node', name: n.name, x: e.clientX, y: e.clientY }); }}
                    role="button"
                    aria-label={`${n.name}: ${n.verdict}`}
                  >
                    <rect x={p.x} y={p.y} width={p.w} height={NODE_H} rx={6} fill={theme.palette.background.paper} />
                    <rect x={p.x} y={p.y} width={p.w} height={NODE_H} rx={6} fill={color} fillOpacity={0.12} stroke={color} strokeWidth={isSelected ? 2.5 : 1.5} />
                    <rect x={p.x} y={p.y} width={5} height={NODE_H} rx={2} fill={color} />
                    <text x={p.x + 12} y={p.y + 19} fontSize={11} fontWeight={600} fill={theme.palette.text.primary}>{n.name}</text>
                    {badge && (
                      <g>
                        <rect x={p.x + p.w - dotsW - badgeW - 8} y={p.y + 8} width={badgeW} height={14} rx={7} fill={color} fillOpacity={0.15} stroke={color} strokeWidth={0.8} />
                        <text x={p.x + p.w - dotsW - badgeW - 8 + 5} y={p.y + 18} fontSize={9} fill={theme.palette.text.primary}>{badge}</text>
                      </g>
                    )}
                    {colorBy === 'source' && srcs.length > 1 && srcs.slice(0, 5).map((s, i) => (
                      <circle key={s} cx={p.x + p.w - 10 - i * 9} cy={p.y + NODE_H / 2} r={3.5} fill={sourceColor.get(s)} />
                    ))}
                  </g>
                );
              })}
            </svg>
          )}
        </Box>
      </Box>
      <Popper
        open={Boolean(menuItem)}
        anchorEl={menu ? { getBoundingClientRect: () => ({ x: menu.x, y: menu.y, top: menu.y, left: menu.x, right: menu.x, bottom: menu.y, width: 0, height: 0, toJSON: () => ({}) }) } : null}
        placement="bottom-start"
        sx={{ zIndex: theme.zIndex.modal }}
      >
        <ClickAwayListener onClickAway={() => setMenu(null)}>
          <Paper elevation={4} sx={{ minWidth: 280 }}>
            {menuItem && (
              <Box sx={{ px: 2, pt: 1.25, pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
                  <Typography variant="caption" fontWeight={700} fontFamily="monospace">{menuItem.name}</Typography>
                  <StatusPill status={statusOf(menu.kind, menuItem, theme, { verdict: verdictColor })} />
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">{statusLine(menu.kind, menuItem)}</Typography>
              </Box>
            )}
            <Divider />
            <MenuList dense autoFocusItem onKeyDown={(e) => { if (e.key === 'Escape') setMenu(null); }}>
              <MenuItem onClick={() => { setMenu(null); setSelected({ kind: menu.kind, name: menu.name }); }}>
                <ListItemIcon><LightbulbOutlinedIcon fontSize="small" /></ListItemIcon>
                <ListItemText>{t('data.lightUpWhatItReadsAndWhomItHolds')}</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { setMenu(null); setQuery(menu.name); }}>
                <ListItemIcon><FilterAltOutlinedIcon fontSize="small" /></ListItemIcon>
                <ListItemText>{t('data.showOnlyThisBranch')}</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { setMenu(null); navigator.clipboard?.writeText(menu.name); }}>
                <ListItemIcon><ContentCopyOutlinedIcon fontSize="small" /></ListItemIcon>
                <ListItemText>{t('data.copyTheName')}</ListItemText>
              </MenuItem>
              {menu?.kind === 'leaf' && menuItem?.pipeline?.public_id && <Divider sx={{ my: 0.5 }} />}
              {menu?.kind === 'leaf' && menuItem?.pipeline?.public_id && (
                <MenuItem onClick={() => { setMenu(null); navigate(`/console/pipelines/${menuItem.pipeline.public_id}`); }}>
                  <ListItemIcon><TimelineOutlinedIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>{t('data.openThePipeline')}</ListItemText>
                </MenuItem>
              )}
              {menu?.kind === 'node' && onReset && <Divider sx={{ my: 0.5 }} />}
              {menu?.kind === 'node' && onReset && (
                <ListSubheader disableSticky sx={{ lineHeight: '24px', fontSize: 10.5 }}>{t('data.thePlannerRebuildsItOnTheNextTick')}</ListSubheader>
              )}
              {menu?.kind === 'node' && onReset && (
                <MenuItem onClick={() => { setMenu(null); onReset({ node: menuItem, descendants: [] }); }}>
                  <ListItemIcon><RestartAltOutlinedIcon fontSize="small" color="error" /></ListItemIcon>
                  <ListItemText>{t('data.resetThisNode')}</ListItemText>
                </MenuItem>
              )}
              {menu?.kind === 'node' && onReset && (
                <MenuItem
                  onClick={() => {
                    setMenu(null);
                    onReset({ node: menuItem, descendants: [...full.downstreamNodes([menu.name])].sort(), withDescendants: true });
                  }}
                >
                  <ListItemIcon><AccountTreeOutlinedIcon fontSize="small" color="error" /></ListItemIcon>
                  <ListItemText>{t('data.resetThisNodeAndEverythingBelow')}</ListItemText>
                </MenuItem>
              )}
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}
